// src/services/aeo/clientDocs.ts
//
// Phase 1 documents: Oura reads each client's Google Docs folder and shows
// what is waiting, on whom, and since when. This is the one module screens
// import for it (types, calls and the change broadcast).
//
// Public API
//   isDocsLive                               true only when VITE_ENABLE_OURA_DOCS_LIVE is exactly "true"
//   isDocsSample                             the sample store answers (docs switch off, Oura on fixtures)
//   getDocsConnection()                      GET    /documents/connection               -> DocsConnection
//   getDocsFolder(businessId)                GET    /businesses/{id}/docs-folder        -> DocsFolder | null (null = not linked)
//   linkDocsFolder(businessId, url)          PUT    /businesses/{id}/docs-folder {url}  -> DocsFolder
//   createDocsFolder(businessId)             POST   /businesses/{id}/docs-folder/create -> DocsFolderCreated (makes it in Drive, or links the one there)
//   unlinkDocsFolder(businessId)             DELETE /businesses/{id}/docs-folder        -> {unlinked: true}
//   readDocsFolderNow(businessId)            POST   /businesses/{id}/docs-folder/read   -> {queued: true}
//   listBusinessDocuments(businessId)        GET    /businesses/{id}/documents          -> BusinessDocuments
//   getDocument(documentId)                  GET    /documents/{id}                     -> DocumentDetail
//   pickDocumentCodes(documentId, pick)      PUT    /documents/{id}/codes               -> StaffDocument
//   setDocumentReplyBy(documentId, date)     PATCH  /documents/{id} {replyBy}           -> StaffDocument (null = back to the default)
//   trashDocument(documentId, confirmName)   POST   /documents/{id}/trash {confirmName}  -> TrashedDocument (to the Drive trash)
//   getDocsAcrossClients()                   GET    /documents/summary                  -> DocsAcrossClients
//   subscribeDocs(cb)                        told after any write here or in clientDocsWrite.ts, so open screens reload
//   announceDocsChanged()                    the broadcast itself, for the write service
//   parseDriveFolderLink(raw)                check a pasted link before sending it
//   codeChipsOf(codes, outlet)               a doc's code chips, "S2-MK"
//   joinCodes(labels)                        codes as Oura writes them, "S1, S3, B1, G4"
//
// Every call resolves to a result and never throws: `{ok: true, data}` or a
// `DocsFailure` whose `reason` says which failure it was (network,
// unauthorised, forbidden, notFound, notServed, rejected) and, when the server
// refused, its own `code` (folder_url_invalid, folder_not_readable,
// folder_linked_elsewhere, docs_not_configured, unknown_code, outlet_invalid,
// codes_in_name, reply_by_in_past). So a screen can show loading, empty,
// error and "not available yet" as different things.
//
// With the switch unset, a local run (VITE_ENABLE_OURA_LIVE_API unset too)
// serves the sample store in clientDocsMock.ts; its dev tools and fixtures
// are described at the top of that file. Staging and production with the
// switch unset answer `notServed` on every call, never sample data.

import axios, { type AxiosResponse } from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import type {
    BusinessDocumentsResult,
    DocsAcrossClientsResult,
    DocsConnectionResult,
    DocsErrorCode,
    DocsFailure,
    DocsFolder,
    DocsFolderCreated,
    DocsFolderResult,
    DocsResult,
    DocumentDetailResult,
    PickCodeIn,
    StaffDocumentResult,
} from "./clientDocsTypes";
import { DOCS_ERROR_CODES } from "./clientDocsTypes";
import { DOCS_SAMPLE_SERVES, docsFailure, docsMock, setDocsMockListener } from "./clientDocsMock";
// The write sample (phase 2) adds its Drive fixtures to the same store, so
// every documents screen reads one sample whichever screen loads first. It
// registers nothing unless the write sample serves (the dev server only).
import "./clientDocsWriteMock";

export * from "./clientDocsTypes";
export { parseDriveFolderLink, codeChipsOf, joinCodes, type FolderLinkCheck } from "./clientDocsRules";
export { docsFailure } from "./clientDocsMock";

/**
 * The documents service's own switch. Absent means the sample store: staging
 * already sets the other Oura live switches, and gating on those would send
 * it to routes that do not exist yet.
 */
export const isDocsLive = import.meta.env.VITE_ENABLE_OURA_DOCS_LIVE === "true";

/**
 * Whether the sample store answers: only where Oura itself runs on fixtures
 * (VITE_ENABLE_OURA_LIVE_API unset, every local run). On staging and
 * production with the docs switch off, every call answers `notServed`, so
 * staff read "not available yet" and never see a fixture client, and the
 * merchant page carries no documents.
 */
export const isDocsSample = DOCS_SAMPLE_SERVES;

/** The sample store's answer, or "not available yet" on a live environment. */
function sample<T>(run: () => Promise<DocsResult<T>>): Promise<DocsResult<T>> {
    return isDocsSample ? run() : Promise.resolve(docsFailure("notServed"));
}

const AEO_BASE = `${import.meta.env.VITE_TALENT_AI_BE_BASE_URL}/api/v1`;
const enc = encodeURIComponent;

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/* ------------------------------------------------------------------------- *
 * Change broadcast
 * ------------------------------------------------------------------------- */

const listeners = new Set<() => void>();

/** Be told after any documents write (and any sample-store dev action). Returns the unsubscribe. */
export function subscribeDocs(cb: () => void): () => void {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
}

function emitDocsChanged(): void {
    // A copy, so a listener that unsubscribes while being told does not skip the next one.
    Array.from(listeners).forEach((cb) => cb());
}

/** Tell every open documents screen to read again: the write service (clientDocsWrite.ts) calls it after a live write. */
export const announceDocsChanged = emitDocsChanged;

setDocsMockListener(emitDocsChanged);

/* ------------------------------------------------------------------------- *
 * Live calls
 * ------------------------------------------------------------------------- */

interface Envelope {
    code?: unknown;
    message?: unknown;
    errors?: unknown;
    data?: unknown;
    detail?: unknown;
}

const isKnownCode = (v: unknown): v is DocsErrorCode => typeof v === "string" && (DOCS_ERROR_CODES as readonly string[]).includes(v);

/** ambiguous_folder: the folders that share the name, each with its link. */
function parseFolders(raw: unknown): { id: string; url: string }[] | null {
    if (!Array.isArray(raw)) return null;
    const out = raw
        .map((f) => (f && typeof f === "object" ? (f as Record<string, unknown>) : null))
        .filter((f): f is Record<string, unknown> => f !== null && typeof f.id === "string" && typeof f.url === "string")
        .map((f) => ({ id: f.id as string, url: f.url as string }));
    return out.length ? out : null;
}

/** Reads a failed request into a DocsFailure: the server's own code when it sent one, else which kind of failure. */
export function readDocsError(err: unknown, what: string): DocsFailure {
    const e = err as { response?: { status?: number; data?: unknown } };
    const res = e.response;
    if (!res) {
        catchErrorLog(`[oura] docs ${what}: no response`);
        return docsFailure("network");
    }
    const status = res.status ?? null;
    const body = (res.data && typeof res.data === "object" ? res.data : {}) as Envelope;
    const inner = (body.data && typeof body.data === "object" ? body.data : {}) as Record<string, unknown>;
    const errors = Array.isArray(body.errors) ? body.errors.filter((x): x is string => typeof x === "string") : [];
    const code = isKnownCode(body.code) ? body.code : errors.find(isKnownCode) ?? (isKnownCode(inner.code) ? inner.code : null);
    const message = typeof body.message === "string" && body.message.trim() ? body.message : null;
    if (code) {
        const linkedTo = code === "folder_linked_elsewhere" && typeof inner.businessId === "string"
            ? { businessId: inner.businessId, name: typeof inner.name === "string" ? inner.name : typeof inner.businessName === "string" ? inner.businessName : null }
            : null;
        return docsFailure("rejected", {
            code,
            status,
            message,
            badCodes: code === "unknown_code" ? errors.filter((x) => !isKnownCode(x)) : null,
            linkedTo,
            existingId: typeof inner.existingId === "string" ? inner.existingId : null,
            folders: parseFolders(inner.folders),
        });
    }
    if (status === 401) return docsFailure("unauthorised", { status });
    if (status === 403) return docsFailure("forbidden", { status });
    // A 404 in the house envelope is "not found"; FastAPI's bare {"detail": "Not Found"} is a route that is not deployed.
    if (status === 404) return "message" in body || "code" in body ? docsFailure("notFound", { status, message }) : docsFailure("notServed", { status });
    if (status === 405 || status === 501) return docsFailure("notServed", { status });
    catchErrorLog(`[oura] docs ${what} failed (${status ?? "no status"})`);
    // The server answered but with a code this build does not know: its own sentence is the reason, not "could not reach".
    return docsFailure("network", { status, message: status ? message ?? `Could not reach the server (HTTP ${status}).` : null });
}

async function call<T>(what: string, run: () => Promise<AxiosResponse>): Promise<DocsResult<T>> {
    try {
        const res = await run();
        return { ok: true, data: (res.data as { data: T }).data };
    } catch (err) {
        return readDocsError(err, what);
    }
}

const opts = () => ({ headers: authHeaders(), timeout: 30_000 });

/** Emit the change broadcast after a write that succeeded. */
function thenEmit<T>(p: Promise<DocsResult<T>>): Promise<DocsResult<T>> {
    return p.then((r) => {
        if (r.ok) emitDocsChanged();
        return r;
    });
}

/* ------------------------------------------------------------------------- *
 * The calls
 * ------------------------------------------------------------------------- */

/** Route 1: the Drive connection as oura@ (status, account, last sync). */
export function getDocsConnection(): Promise<DocsConnectionResult> {
    if (!isDocsLive) return sample(() => docsMock.getDocsConnection());
    return call("connection", () => axios.get(`${AEO_BASE}/documents/connection`, opts()));
}

/** Route 3: the client's folder, or null when none is linked. */
export function getDocsFolder(businessId: string): Promise<DocsFolderResult> {
    if (!isDocsLive) return sample(() => docsMock.getDocsFolder(businessId));
    return call<DocsFolder | null>("folder", () => axios.get(`${AEO_BASE}/businesses/${enc(businessId)}/docs-folder`, opts()));
}

/** Route 4: paste the folder link once. Refusals: folder_url_invalid, folder_not_readable, folder_linked_elsewhere, docs_not_configured. */
export function linkDocsFolder(businessId: string, url: string): Promise<DocsResult<DocsFolder>> {
    const body = { url: url.trim() };
    if (!isDocsLive) return thenEmit(sample(() => docsMock.linkDocsFolder(businessId, body.url)));
    return thenEmit(call<DocsFolder>("link folder", () => axios.put(`${AEO_BASE}/businesses/${enc(businessId)}/docs-folder`, body, opts())));
}

/**
 * Make the client's folder in Drive, or link the one already there (#6178). Refusals:
 * already_linked (the card reloads, no error), ambiguous_folder, projects_folder_missing,
 * docs_folder_busy, write_not_configured, folder_linked_elsewhere. A 404 with no
 * house envelope means the route is not deployed yet: `notServed`.
 */
export function createDocsFolder(businessId: string): Promise<DocsResult<DocsFolderCreated>> {
    if (!isDocsLive) return thenEmit(sample(() => docsMock.createDocsFolder(businessId)));
    return thenEmit(call<DocsFolderCreated>("create folder", () => axios.post(`${AEO_BASE}/businesses/${enc(businessId)}/docs-folder/create`, {}, opts())));
}

/** Route 5: unlink the folder. Its documents leave the board; nothing in Drive changes. */
export function unlinkDocsFolder(businessId: string): Promise<DocsResult<{ unlinked: true }>> {
    if (!isDocsLive) return thenEmit(sample(() => docsMock.unlinkDocsFolder(businessId)));
    return thenEmit(call<{ unlinked: true }>("unlink folder", () => axios.delete(`${AEO_BASE}/businesses/${enc(businessId)}/docs-folder`, opts())));
}

/** Route 6: "Read now". Queued: the read lands on the next poll, not in this answer. */
export function readDocsFolderNow(businessId: string): Promise<DocsResult<{ queued: true }>> {
    if (!isDocsLive) return thenEmit(sample(() => docsMock.readDocsFolderNow(businessId)));
    return thenEmit(call<{ queued: true }>("read folder", () => axios.post(`${AEO_BASE}/businesses/${enc(businessId)}/docs-folder/read`, {}, opts())));
}

/** Route 7: the documents board for one client. */
export function listBusinessDocuments(businessId: string): Promise<BusinessDocumentsResult> {
    if (!isDocsLive) return sample(() => docsMock.listBusinessDocuments(businessId));
    return call("board", () => axios.get(`${AEO_BASE}/businesses/${enc(businessId)}/documents`, opts()));
}

/** Route 8: one doc with every thread, for the drawer. */
export function getDocument(documentId: string): Promise<DocumentDetailResult> {
    if (!isDocsLive) return sample(() => docsMock.getDocument(documentId));
    return call("document", () => axios.get(`${AEO_BASE}/documents/${enc(documentId)}`, opts()));
}

/**
 * Route 9: Pick a code for a file whose name carries none, or mark it
 * internal (`{notClientDoc: true}`). It never renames the file. Refusals:
 * unknown_code (`badCodes`), outlet_invalid, codes_in_name.
 */
export function pickDocumentCodes(documentId: string, pick: PickCodeIn): Promise<StaffDocumentResult> {
    if (!isDocsLive) return thenEmit(sample(() => docsMock.pickDocumentCodes(documentId, pick)));
    return thenEmit(call("pick code", () => axios.put(`${AEO_BASE}/documents/${enc(documentId)}/codes`, pick, opts())));
}

/**
 * Mark a doc done without sharing it, or undo that (backend #6364, #4541).
 * Staff only; nothing is emailed. `doneOn` ("YYYY-MM-DD") is the day the work
 * was done (backend #6414): sent only when given, so with none the server
 * dates it the doc's own date. Refusal: done_on_future. The board re-reads on success.
 */
export function markDocumentDone(documentId: string, done: boolean, doneOn: string | null = null): Promise<StaffDocumentResult> {
    if (!isDocsLive) return thenEmit(sample(() => docsMock.markDocumentDone(documentId, done, doneOn)));
    const url = `${AEO_BASE}/documents/${enc(documentId)}/done`;
    const body = done && doneOn ? { doneOn } : undefined;
    return thenEmit(call(done ? "mark done" : "undo done", () => (done ? axios.post(url, body, opts()) : axios.delete(url, opts()))));
}

/** What `POST /documents/{id}/trash` answers: the file is in Google Drive's trash. */
export interface TrashedDocument {
    id: string;
    name: string;
    driveFileId: string;
    trashed: true;
}

/**
 * Delete a file from the client's folder (backend #6451): it moves to Google
 * Drive's trash (restorable there for 30 days) and leaves the board. Staff
 * only; `confirmName` is the file's name as typed, checked again by the
 * server. Refusals: confirm_name_mismatch, not_file_owner (the message names
 * the owner), file_not_found_in_drive, write_not_configured, drive_unavailable.
 * The board re-reads on success.
 */
export function trashDocument(documentId: string, confirmName: string): Promise<DocsResult<TrashedDocument>> {
    if (!isDocsLive) return Promise.resolve(docsFailure("rejected", { message: "Delete is not in the sample data." }));
    return thenEmit(call<TrashedDocument>("delete", () => axios.post(`${AEO_BASE}/documents/${enc(documentId)}/trash`, { confirmName }, opts())));
}

/** Route 10: change one doc's reply-by ("YYYY-MM-DD"), or null to go back to the default. Refusal: reply_by_in_past. */
export function setDocumentReplyBy(documentId: string, replyBy: string | null): Promise<StaffDocumentResult> {
    if (!isDocsLive) return thenEmit(sample(() => docsMock.setDocumentReplyBy(documentId, replyBy)));
    return thenEmit(call("reply-by", () => axios.patch(`${AEO_BASE}/documents/${enc(documentId)}`, { replyBy }, opts())));
}

/** Route 11: All clients. */
export function getDocsAcrossClients(): Promise<DocsAcrossClientsResult> {
    if (!isDocsLive) return sample(() => docsMock.getDocsAcrossClients());
    return call("summary", () => axios.get(`${AEO_BASE}/documents/summary`, opts()));
}
